/**
 * The runtime's text endpoints. `agent_runtime_lld.md` §40.
 *
 * Stories VC-012 and VC-016. Descended from `src/text.js`, which held the
 * same four calls; `close` is new (RS-018, §40.7) and the rest are
 * unchanged, because the endpoints are.
 *
 * What this file does *not* do is decide anything. The stream framing is
 * `agent-sse`'s (`WasmTextEventStream`), because a `text/event-stream`
 * arrives in arbitrary chunks and an event is not a line — see that module
 * for why the decision lives there rather than here. A second decoder
 * written in JavaScript would be the same bug with no test in this
 * repository able to see it.
 *
 * It was `voice-client-core`'s until V2-000, which is why the chat page's
 * bundle used to carry a voice client to read an event stream.
 *
 * `EventSource` is unusable: the credential is an `Authorization` header and
 * `EventSource` cannot send one. `fetch` can, so the body is read directly.
 */

import { uuid } from "../session/uuid.js";

/**
 * @typedef {{ base: string, token: string, tenant: string, agent: string }}
 *   RuntimeConfig
 */

/**
 * Opens a session and its root agent instance.
 *
 * @param {RuntimeConfig} config
 * @returns {Promise<{ session_id: string, agent_instance_id: string }>}
 */
export async function createSession({ base, token, tenant, agent }) {
  const response = await fetch(`${base}/v1/sessions`, {
    method: "POST",
    headers: {
      "content-type": "application/json",
      authorization: `Bearer ${token}`,
    },
    // `tenant_id` is checked against the credential's tenant rather than
    // trusted (§40.4). It is sent so a mismatch is detectable.
    body: JSON.stringify({ tenant_id: tenant, agent }),
  });
  if (!response.ok) {
    throw new Error(await describe(response));
  }
  return response.json();
}

/**
 * Posts one typed message.
 *
 * The id is the client's, and posting the same one twice creates one
 * message (§9) — so a retry after a dropped connection is safe.
 *
 * @param {RuntimeConfig} config
 * @param {string} sessionId
 * @param {string} text
 */
export async function postMessage({ base, token }, sessionId, text) {
  const response = await fetch(`${base}/v1/sessions/${sessionId}/messages`, {
    method: "POST",
    headers: {
      "content-type": "application/json",
      authorization: `Bearer ${token}`,
    },
    // `uuid()` and not `crypto.randomUUID()`: the latter is secure-context
    // only, and this client is reached over plain HTTP from a phone on the
    // same network. See `session/uuid.js`.
    body: JSON.stringify({ message_id: uuid(), text }),
  });
  if (!response.ok) {
    throw new Error(await describe(response));
  }
  return response.json();
}

/**
 * Ends a session. §40.7, story RS-018.
 *
 * Idempotent by the method's own promise, and answering with the state that
 * stands — so a caller that is not sure whether its last attempt landed
 * simply calls again. Which is why this does not throw on a 404: a session
 * that is not there is a session that is not running, which is what the
 * caller wanted.
 *
 * @param {RuntimeConfig} config
 * @param {string} sessionId
 * @returns {Promise<{ session_id: string, status: string } | null>}
 */
export async function closeSession({ base, token }, sessionId) {
  const response = await fetch(`${base}/v1/sessions/${sessionId}`, {
    method: "DELETE",
    headers: { authorization: `Bearer ${token}` },
  });
  if (response.status === 404) {
    return null;
  }
  if (!response.ok) {
    throw new Error(await describe(response));
  }
  return response.json();
}

/**
 * The conversation so far. `agent_runtime_lld.md` §40.9.
 *
 * Pages to the end and returns one list, because a caller wants the
 * conversation and not a cursor. It stops on `next_after_sequence` being
 * absent and never on an empty page: the projection drops entries, so a page
 * of tool results projects to nothing while the conversation continues after
 * it (§40.9.5).
 *
 * `MAX_PAGES` is a bound on a server that never terminates its own cursor.
 * Without it a client loops forever on a defect it cannot see, which is a
 * worse failure than an error message.
 *
 * @param {RuntimeConfig} config
 * @param {string} sessionId
 * @param {{ signal?: AbortSignal, limit?: number }} [options]
 * @returns {Promise<any[]>} the entries, in the order they were said
 */
export async function readTranscript(
  { base, token },
  sessionId,
  { signal, limit } = {},
) {
  const entries = [];
  let after = null;
  for (let page = 0; page < MAX_PAGES; page += 1) {
    const query = new URLSearchParams();
    if (after !== null) {
      query.set("after_sequence", String(after));
    }
    if (limit) {
      query.set("limit", String(limit));
    }
    const suffix = query.toString() ? `?${query}` : "";
    const response = await fetch(
      `${base}/v1/sessions/${sessionId}/transcript${suffix}`,
      { headers: { authorization: `Bearer ${token}` }, signal },
    );
    if (!response.ok) {
      throw new Error(await describe(response));
    }
    const body = await response.json();
    entries.push(...(body.entries ?? []));
    if (
      body.next_after_sequence === undefined ||
      body.next_after_sequence === null
    ) {
      return entries;
    }
    after = body.next_after_sequence;
  }
  throw new Error(`the transcript did not end after ${MAX_PAGES} pages`);
}

/** How many pages `readTranscript` will follow before giving up. */
const MAX_PAGES = 200;

/**
 * Reads the session's event stream until it ends or `signal` aborts.
 *
 * Each payload is one `StreamEnvelope` — it carries its own `event_type` and
 * `event_id`, which is why the SSE frame's own fields are not surfaced.
 *
 * `lastEventId` resumes: §40.3 replays the latest durable snapshot plus the
 * events after it, and an id this deployment no longer recognises is a full
 * resume rather than an error. So a caller may always pass what it has.
 *
 * @param {RuntimeConfig} config
 * @param {string} sessionId
 * @param {{ decoder: { push(chunk: Uint8Array): string[] },
 *           onEvent: (envelope: any) => void,
 *           lastEventId?: string | null,
 *           signal: AbortSignal }} sink
 */
export async function readEvents(
  { base, token },
  sessionId,
  { decoder, onEvent, lastEventId, signal },
) {
  const query = lastEventId
    ? `?last_event_id=${encodeURIComponent(lastEventId)}`
    : "";
  const response = await fetch(
    `${base}/v1/sessions/${sessionId}/events${query}`,
    {
      headers: {
        accept: "text/event-stream",
        authorization: `Bearer ${token}`,
      },
      signal,
    },
  );
  if (!response.ok || !response.body) {
    throw new Error(await describe(response));
  }

  const reader = response.body.getReader();
  for (;;) {
    const { done, value } = await reader.read();
    if (done) {
      return;
    }
    for (const payload of decoder.push(value)) {
      let envelope;
      try {
        envelope = JSON.parse(payload);
      } catch {
        // A frame that will not parse is dropped rather than fatal: the
        // stream is still framed correctly, and the next event is readable.
        // Framing failures are the decoder's and throw from `push`.
        continue;
      }
      onEvent(envelope);
    }
  }
}

/**
 * What went wrong, from the error body the transport sends.
 *
 * The body carries a stable label and nothing else (§40.4), so this reports
 * the label and the status rather than inventing a sentence for each one —
 * and a sentence invented here would be a second vocabulary that drifts
 * from the server's the first time a label is added.
 *
 * @param {Response} response
 */
async function describe(response) {
  let label = "";
  try {
    label = (await response.json()).error ?? "";
  } catch {
    // No body, or not JSON. The status is still worth reporting.
  }
  return label ? `${response.status} ${label}` : `${response.status}`;
}
